import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import type { Option } from '@openx/types/options';
import { exhaustiveCheck } from '@openx/utils/lib/exhaustiveCheck';
import type React from 'react';
import { useCallback } from 'react';
import type { FiltersContext } from '../../../context/types';
import { FILTER_TYPES, type FilterCriteria, type FilterValue } from '../../types';
import { isOptionArray, isString, isStringArray, isStringOrUndefinedTuple } from '../../utils/typeGuards';
import { validateAndGetOptions } from '../../utils/validateOptions';
import { AutocompleteSelector, type AutocompleteSelectorRef } from './AutocompleteSelector';
import {
  AutocompleteSelectorVirtualized,
  type AutocompleteSelectorVirtualizedRef,
} from './AutocompleteSelectorVirtualized';
import { CheckboxSelector } from './CheckboxSelector';
import { DropdownSelector } from './DropdownSelector';
import { DateRangeSelector } from './dateRangeSelector/DateRangeSelector';
import { GreaterLessSelector } from './GreaterLessSelector';
import { HierarchicalCheckboxSelector } from './HierarchicalCheckboxSelector';
import { MultiOptionWithRadioFilter } from './MultiOptionWithRadioFilter';
import { MultiOptionWithRangeFilter } from './MultiOptionWithRangeFilter';
import { MultiSelectWithRadioFilter } from './MultiSelectWithRadioFilter';
import { NumericInputSelector } from './NumericInputSelector';
import { RadioSelector } from './RadioSelector';
import { RangeSelector } from './RangeSelector';
import { RangeWithRadioFilter } from './RangeWithRadioFilter';
import { SearchAndSelectFilter } from './SearchAndSelectFilter';
import { SwitchSelector } from './SwitchSelector';
import { TextInputSelector } from './TextInputSelector';

type FilterContentRendererProps<Criteria extends FilterCriteria> = {
  appliedFilters?: FilterCriteria;
  filter: FiltersContext<Criteria>['filtersConfig'][number];
  onOptionChange: (key: string, options: FilterValue) => void;
  isDisabled?: boolean;
  autocompleteRef?: React.Ref<AutocompleteSelectorRef> | React.Ref<AutocompleteSelectorVirtualizedRef>;
  options?: Option[];
  setValidationState?: (value: boolean) => void;
  onInnerPopoverOpen: (open: boolean) => void;
  isLoading?: boolean;
};

export const FilterContentRenderer = <Criteria extends FilterCriteria>({
  appliedFilters,
  autocompleteRef,
  options,
  setValidationState,
  onOptionChange,
  filter,
  isDisabled,
  onInnerPopoverOpen,
  isLoading,
}: FilterContentRendererProps<Criteria>) => {
  const { key: filterKey } = filter;

  const onChangeHandler = useCallback(
    (value: string | string[]) => {
      onOptionChange(filterKey, value);
    },
    [onOptionChange, filterKey]
  );

  const handleRangeChange = useCallback(
    (value?: [string | undefined, string | undefined]) => {
      const newValue = value ?? [undefined, undefined];

      onOptionChange(filterKey, newValue);
    },
    [onOptionChange, filterKey]
  );

  const selectedOptions = appliedFilters?.[filterKey];

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress size={20} />
      </Box>
    );
  }

  switch (filter.type) {
    case FILTER_TYPES.RADIO: {
      const validOptions = validateAndGetOptions<string>(selectedOptions, isString, '', filter.type);

      return (
        <RadioSelector
          options={options}
          onOptionChange={onChangeHandler}
          selectedOptions={validOptions}
          isDisabled={isDisabled}
          orientation={filter.orientation ?? 'column'}
          defaultOption={filter.defaultOption ?? ''}
        />
      );
    }

    case FILTER_TYPES.SWITCH: {
      const validOptions = validateAndGetOptions<string>(selectedOptions, isString, '', filter.type);

      return <SwitchSelector option={options} onOptionChange={onChangeHandler} selectedOption={validOptions} />;
    }

    case FILTER_TYPES.CHECKBOX: {
      const validOptions = validateAndGetOptions<string[]>(selectedOptions, isStringArray, [], filter.type);

      return (
        <CheckboxSelector
          options={options}
          onOptionChange={onChangeHandler}
          isDisabled={isDisabled}
          selectedOptions={validOptions}
        />
      );
    }

    case FILTER_TYPES.DROPDOWN: {
      const validOptions = validateAndGetOptions<string>(selectedOptions, isString, '', filter.type);

      return (
        <DropdownSelector
          options={options}
          onOptionChange={onChangeHandler}
          isDisabled={isDisabled}
          selectedOptions={validOptions}
          config={filter.config}
        />
      );
    }

    case FILTER_TYPES.HIERARCHICAL_CHECKBOX: {
      const validOptions = validateAndGetOptions<string[]>(selectedOptions, isStringArray, [], filter.type);

      return (
        <HierarchicalCheckboxSelector
          options={options}
          onOptionChange={onChangeHandler}
          isDisabled={isDisabled}
          selectedOptions={validOptions}
        />
      );
    }

    case FILTER_TYPES.AUTOCOMPLETE: {
      const validOptions = validateAndGetOptions<Option[]>(options, isOptionArray, [], filter.type);
      const validSelectedOptions = validateAndGetOptions<string[]>(selectedOptions, isStringArray, [], filter.type);

      if (validOptions.length > 100) {
        return (
          <AutocompleteSelectorVirtualized
            ref={autocompleteRef as React.Ref<AutocompleteSelectorVirtualizedRef>}
            options={validOptions}
            selectedOptions={validSelectedOptions}
            onOptionChange={onChangeHandler}
            config={filter.config}
            isDisabled={isDisabled}
          />
        );
      }

      return (
        <AutocompleteSelector
          ref={autocompleteRef as React.Ref<AutocompleteSelectorRef>}
          options={validOptions}
          selectedOptions={validSelectedOptions}
          onOptionChange={onChangeHandler}
          config={filter.config}
          isDisabled={isDisabled}
        />
      );
    }

    case FILTER_TYPES.MULTI_OPTION_WITH_RANGE: {
      const validOptions = validateAndGetOptions<Option[]>(options, isOptionArray, [], filter.type);

      return (
        <MultiOptionWithRangeFilter
          options={validOptions}
          appliedFilters={appliedFilters}
          onOptionsChange={onOptionChange}
          config={filter.config}
        />
      );
    }

    case FILTER_TYPES.DATE_RANGE: {
      const validOptions = validateAndGetOptions<[string | undefined, string | undefined]>(
        selectedOptions,
        isStringOrUndefinedTuple,
        [undefined, undefined],
        filter.type
      );

      return (
        <DateRangeSelector
          filterKey={filterKey}
          inputFormat={filter.config?.inputFormat}
          inputPlaceholder={filter.config?.inputPlaceholder}
          maxDate={filter.config?.maxDate}
          minDate={filter.config?.minDate}
          onDateChange={handleRangeChange}
          onInnerPopoverOpen={onInnerPopoverOpen}
          selectedOptions={validOptions}
          setValidationState={setValidationState}
        />
      );
    }

    case FILTER_TYPES.MULTI_OPTION_WITH_RADIO: {
      const validOptions = validateAndGetOptions<Option[]>(options, isOptionArray, [], filter.type);

      return (
        <MultiOptionWithRadioFilter
          options={validOptions}
          appliedFilters={appliedFilters}
          onOptionsChange={onOptionChange}
          config={filter.config}
        />
      );
    }

    case FILTER_TYPES.MULTI_SELECT_WITH_RADIO: {
      const validOptions = validateAndGetOptions<Option[]>(options, isOptionArray, [], filter.type);

      return (
        <MultiSelectWithRadioFilter
          options={validOptions}
          appliedFilters={appliedFilters}
          onOptionsChange={onOptionChange}
          config={filter.config}
          filterKey={filterKey}
        />
      );
    }

    case FILTER_TYPES.RANGE: {
      const validOptions = validateAndGetOptions<[string | undefined, string | undefined]>(
        selectedOptions,
        isStringOrUndefinedTuple,
        [undefined, undefined],
        filter.type
      );

      return (
        <RangeSelector
          filterKey={filterKey}
          isDisabled={isDisabled}
          config={filter.config}
          setValidationState={setValidationState}
          selectedOptions={validOptions}
          onOptionChange={handleRangeChange}
        />
      );
    }

    case FILTER_TYPES.GREATER_LESS: {
      if (typeof selectedOptions !== 'undefined' && typeof selectedOptions !== 'string') {
        throw new Error(`selectedOptions must be a string for ${FILTER_TYPES.GREATER_LESS} type`);
      }

      return (
        <GreaterLessSelector
          options={options}
          config={filter.config}
          selectedOptions={selectedOptions}
          onOptionChange={onChangeHandler}
        />
      );
    }

    case FILTER_TYPES.NUMERIC_INPUT: {
      const validOptions = validateAndGetOptions<string>(selectedOptions, isString, '', filter.type);

      return (
        <NumericInputSelector
          filterKey={filterKey}
          config={filter.config}
          selectedOptions={validOptions}
          onOptionChange={onChangeHandler}
          setValidationState={setValidationState}
        />
      );
    }

    case FILTER_TYPES.TEXT_INPUT: {
      const validOptions = validateAndGetOptions<string>(selectedOptions, isString, '', filter.type);

      return (
        <TextInputSelector
          filterKey={filterKey}
          config={filter.config}
          selectedOptions={validOptions}
          onOptionChange={onChangeHandler}
          setValidationState={setValidationState}
        />
      );
    }

    case FILTER_TYPES.RANGE_WITH_RADIO: {
      return (
        <RangeWithRadioFilter appliedFilters={appliedFilters} onOptionsChange={onOptionChange} config={filter.config} />
      );
    }

    case FILTER_TYPES.FORMAT_AND_DEVICES: {
      return filter.config.renderContent({
        appliedFilters,
        onOptionChange,
        isDisabled,
        onInnerPopoverOpen,
      });
    }

    case FILTER_TYPES.SEARCH_AND_SELECT: {
      const validOptions = validateAndGetOptions<Option[]>(options, isOptionArray, [], filter.type);
      const validSelectedOptions = validateAndGetOptions<string>(selectedOptions, isString, '', filter.type);

      return (
        <SearchAndSelectFilter
          isLoading={filter.isLoading}
          noOptionsText={filter.noOptionsText}
          onInputChange={filter.onInputChange}
          onOptionChange={onChangeHandler}
          options={validOptions}
          selectedOption={validSelectedOptions}
        />
      );
    }

    default:
      exhaustiveCheck(filter);
  }
};
